import React from "react";
import { graphql } from "gatsby";
import { Helmet } from "react-helmet";

import Layout from "../../components/Layout";

import Hero from "../../components/Hero";
import TrustedMediaList from "../../components/TrustedMediaList";
import SideContentTemplate from "../../components/ContentComponents/SideContentTemplate";
import GetOnBoard from "../../components/ContentComponents/GetOnBoard";
import WithI18next from "../../components/WithI18next";
import Faq, { convertSupportCentreToFaqs } from "../../components/Faq";
import {
  Hero as NewHero,
  GetStarted,
  ReferFriend,
  SpecialGifts,
  ProgrammeTermsConditions,
} from "../../components/EarnReferral";
import { getAcfPartialContent, formatAcfPartial } from "../../utils";
import { checkInstance, INSTANCES } from "../../utils/featureFlag";

import "../../components/EarnReferral/style.scss";

const EarnReferralPage = ({ data, t, lang }) => {
  const isShowNewReferral = checkInstance(
    [INSTANCES.ID, INSTANCES.TH],
    lang.split("_")[0]
  );

  return (
    <Layout
      title={t("earnReferral.seo.title")}
      description={t("earnReferral.seo.description")}
      limeSignUpButton={true}
    >
      <Helmet>
        <body id="earn-referral-page" className="earn" />
      </Helmet>

      {isShowNewReferral ? (
        <NewHero content={data.heroContent.edges[0].node.acfHeroSections} />
      ) : (
        <Hero
          classNames="referral-page"
          backgroundColor="lime"
          content={data.heroContent}
        />
      )}

      <SpecialGifts content={data.specialGiftsContent} />

      <ReferFriend t={t} img={data.imageReferAFriend} />

      {isShowNewReferral && <GetStarted />}

      <ProgrammeTermsConditions content={data.programmeTermsConditions} />

      {data.whatIsZmtContent?.edges?.length > 0 && (
        <SideContentTemplate
          id="earn-referral-download-whitepaper"
          {...formatAcfPartial(data.whatIsZmtContent)}
          signupBtnLabel={t("zmt.whatIsZMT.btnDownloadWhitePaper")}
          signupBtnLinkOverride={t("zmt.whatIsZMT.hrefWhitePaper")}
          signupBtnClassNames={
            "button-primary-lime button-arrow button-rounded"
          }
          isContentCentered={true}
          isContentReversed={true}
        />
      )}

      {data.whatIsZiplockContent?.edges?.length > 0 && (
        <SideContentTemplate
          id="earn-referral-what-is"
          {...formatAcfPartial(data.whatIsZiplockContent)}
          backgroundImg={"primary"}
          isImgRotated={true}
          signupBtnClassNames={
            "button-primary-lime button-arrow button-rounded"
          }
        />
      )}

      {data.assetSecurityContent?.edges?.length > 0 && (
        <SideContentTemplate
          id="earn-referral-asset-security"
          {...formatAcfPartial(data.assetSecurityContent)}
          signupBtnClassNames={
            "button-secondary-lime button-arrow button-rounded"
          }
          isImgHiddenOnMobile={false}
          isContentReversed={true}
          isContentCentered={true}
          isPartialCentered={true}
        />
      )}

      <Faq
        id="earn-referral-faq"
        content={convertSupportCentreToFaqs(data.faqContent)}
      />

      <TrustedMediaList links={data.mediaLinks} />

      <GetOnBoard
        content={getAcfPartialContent(data.earnMoreContent)}
        btnClassNames="button-primary-lime button-arrow button-rounded"
      />
    </Layout>
  );
};

export default WithI18next()(EarnReferralPage);

export const query = graphql`
  query (
    $wpLocale: String!
    $wpGlobalConfig: String!
    $isIndo: Boolean = false
  ) {
    ...MediaLinks
    ...EarnMoreContent

    heroContent: allWpHeroSectionItem(
      filter: {
        language: { locale: { eq: $wpLocale } }
        acfGlobalConfiguration: { gcLinkTo: { eq: $wpGlobalConfig } }
        acfHeroSections: { hsPage: { eq: "ziplock-earn-referral" } }
      }
    ) {
      edges {
        node {
          acfHeroSections {
            hsMainHeading
            hsSubHeading
            hsMainText
            hsButtonText
            hsButtonLink
          }
        }
      }
    }

    specialGiftsContent: allWpPartialItem(
      filter: {
        acfGlobalConfiguration: { gcLinkTo: { eq: ".all" } }
        acfPartials: { pPartialId: { eq: "referral-special-gifts" } }
        language: { locale: { eq: "en_US" } }
      }
      sort: { order: ASC, fields: date }
    ) @skip(if: $isIndo) {
      edges {
        node {
          acfPartials {
            pHeading
            pIcon {
              localFile {
                publicURL
              }
            }
          }
        }
      }
    }

    imageReferAFriend: file(relativePath: { eq: "images/refer-a-friend.png" }) {
      publicURL
    }

    whatIsZmtContent: allWpPartialItem(
      filter: {
        acfGlobalConfiguration: { gcLinkTo: { in: [".all", $wpGlobalConfig] } }
        acfPartials: { pPartialId: { eq: "referral-what-is-zmt" } }
        language: { locale: { eq: $wpLocale } }
      }
      sort: { order: ASC, fields: date }
    ) {
      edges {
        node {
          acfPartials {
            pHeading
            pCssClasses
            pContent
            pIcon {
              localFile {
                publicURL
                childImageSharp {
                  gatsbyImageData
                }
              }
            }
          }
        }
      }
    }

    whatIsZiplockContent: allWpPartialItem(
      filter: {
        acfGlobalConfiguration: { gcLinkTo: { in: [".all", $wpGlobalConfig] } }
        acfPartials: { pPartialId: { eq: "referral-what-is-ziplock" } }
        language: { locale: { eq: $wpLocale } }
      }
      sort: { order: ASC, fields: date }
    ) {
      edges {
        node {
          acfPartials {
            pHeading
            pCssClasses
            pContent
            pIcon {
              localFile {
                publicURL
              }
            }
            pButtonText
            pButtonUrl
          }
        }
      }
    }

    assetSecurityContent: allWpPartialItem(
      filter: {
        acfGlobalConfiguration: { gcLinkTo: { eq: $wpGlobalConfig } }
        acfPartials: { pPartialId: { eq: "asset-security-with-zipmex" } }
        language: { locale: { eq: $wpLocale } }
      }
      sort: { order: ASC, fields: date }
    ) {
      edges {
        node {
          acfPartials {
            pHeading
            pCssClasses
            pContent
            pIcon {
              localFile {
                publicURL
              }
            }
            pButtonText
            pButtonUrl
          }
        }
      }
    }

    programmeTermsConditions: allWpPartialItem(
      filter: {
        acfGlobalConfiguration: { gcLinkTo: { eq: $wpGlobalConfig } }
        acfPartials: { pPartialId: { eq: "referral-terms-and-conditions" } }
        language: { locale: { eq: $wpLocale } }
      }
    ) @include(if: $isIndo) {
      edges {
        node {
          acfPartials {
            pHeading
            pContent
          }
        }
      }
    }

    faqContent: allWpSupportCenterItem(
      filter: {
        language: { locale: { eq: $wpLocale } }
        acfGlobalConfiguration: { gcLinkTo: { in: [".all", $wpGlobalConfig] } }
        categories: {
          nodes: { elemMatch: { slug: { eq: "referral-program" } } }
        }
      }
      sort: { order: DESC, fields: date }
    ) {
      edges {
        node {
          title
          content
        }
      }
    }
  }
`;
